// Offline mode engine: per-device settings, incremental sync, media cache.
import { api, API, registerMediaCache } from "../api";
import { offlineStore, QuotaError } from "./offlineStore";

export const isNativeApp = () => {
  try { return !!(window.Capacitor && window.Capacitor.isNativePlatform && window.Capacitor.isNativePlatform()); } catch (e) { return false; }
};

// ---- per-device settings (localStorage: the choice belongs to THIS phone) ----
const settingsKey = (uid) => `osc_offline_settings_${uid}`;
export const HISTORY_OPTIONS = ["90d", "12m", "all"];
export function getOfflineSettings(uid) {
  try { return JSON.parse(localStorage.getItem(settingsKey(uid)) || "null"); } catch (e) { return null; }
}
export function saveOfflineSettings(uid, s) {
  localStorage.setItem(settingsKey(uid), JSON.stringify(s));
  emit("settings", s);
}
export const offlineEnabled = (uid) => !!getOfflineSettings(uid)?.enabled;
// first login on a native phone → ask
export const needsOfflineChoice = (uid) => isNativeApp() && getOfflineSettings(uid) === null;

function historyStart(history) {
  if (history === "all") return "1970-01-01T00:00:00Z";
  const d = new Date();
  if (history === "12m") d.setMonth(d.getMonth() - 12); else d.setDate(d.getDate() - 90);
  return d.toISOString();
}

// ---- events ----
const listeners = new Set();
export const state = { syncing: false, progress: 0, label: "", lastSync: null, full: false };
function emit(type, detail) { listeners.forEach((fn) => { try { fn(type, detail); } catch (e) { /* ignore */ } }); }
export function subscribeOffline(fn) { listeners.add(fn); return () => listeners.delete(fn); }
function setState(patch) { Object.assign(state, patch); emit("state", { ...state }); }
if (typeof window !== "undefined") window.__oscSimulateStorageFull = () => { setState({ full: true, syncing: false }); emit("full", {}); };

// ---- media cache: object URLs for cached blobs (sync lookup used by mediaSrc) ----
const urlMap = new Map(); // id -> {url, quality}
let hydrated = null;
export function hydrateMedia() {
  if (hydrated) return hydrated;
  hydrated = offlineStore.getAllMedia().then((list) => {
    list.forEach((m) => { if (m.blob) urlMap.set(m.id, { url: URL.createObjectURL(m.blob), quality: m.quality }); });
    emit("media", { count: urlMap.size });
  }).catch(() => {});
  return hydrated;
}
export function cachedMediaUrl(id) {
  const hit = id && urlMap.get(id);
  if (!hit) return null;
  const offline = typeof navigator !== "undefined" && navigator.onLine === false;
  // thumbnails only stand in while offline; full copies are always good
  return (hit.quality === "full" || offline) ? hit.url : null;
}
function forgetMediaUrl(id) { const h = urlMap.get(id); if (h) { try { URL.revokeObjectURL(h.url); } catch (e) { /* ignore */ } urlMap.delete(id); } }
registerMediaCache(cachedMediaUrl);

// ---- quota handling ----
async function guard(promise) {
  try { return await promise; } catch (e) {
    if (e instanceof QuotaError || e?.name === "QuotaExceededError") { setState({ full: true, syncing: false }); emit("full", {}); throw e; }
    throw e;
  }
}

// ---- mirror helpers used by the screens (write-through) ----
export async function mirrorConversations(uid, list) {
  if (!offlineEnabled(uid)) return;
  try { await guard(offlineStore.putConversations(list)); } catch (e) { /* surfaced via "full" */ }
}
export async function mirrorMessages(uid, list) {
  if (!offlineEnabled(uid) || !list?.length) return;
  try { await guard(offlineStore.putMessages(list)); } catch (e) { /* surfaced via "full" */ }
}
export async function patchMirrored(uid, patch) {
  if (!offlineEnabled(uid) || !patch?.id) return;
  try {
    const list = await offlineStore.getMessages(patch.conversation_id || "");
    const cur = list.find((m) => m.id === patch.id) || (await offlineStore.findMessage?.(patch.id));
    if (cur) await offlineStore.putMessages([{ ...cur, ...patch }]);
  } catch (e) { /* ignore */ }
}
export async function forgetMessage(uid, id) { if (offlineEnabled(uid)) offlineStore.deleteMessage(id).catch(() => {}); }
export const readConversations = () => offlineStore.getConversations().catch(() => []);
export const readConversation = (id) => readConversations().then((l) => l.find((c) => c.id === id) || null);
export const readMessages = (convId) => offlineStore.getMessages(convId).catch(() => []);

// ---- media download for a batch of messages ----
async function cacheMediaFor(msgs, settings) {
  const wanted = [];
  for (const m of msgs) {
    for (const md of m.media || []) {
      if (!md?.id) continue;
      if (md.kind === "image") wanted.push({ id: md.id, conv: m.conversation_id, kind: "image", quality: settings.photos === "full" ? "full" : "thumb", created_at: m.created_at });
      else if (md.kind === "audio" || m.type === "voice") wanted.push({ id: md.id, conv: m.conversation_id, kind: "audio", quality: "full", created_at: m.created_at });
    }
  }
  if (!wanted.length) return;
  const have = new Set(await offlineStore.getMediaKeys().catch(() => []));
  const todo = wanted.filter((w) => !have.has(w.id) || (w.quality === "full" && urlMap.get(w.id)?.quality === "thumb"));
  let i = 0;
  const worker = async () => {
    while (i < todo.length && !state.full) {
      const w = todo[i++];
      try {
        const url = w.quality === "thumb" ? `${API}/media/${w.id}/thumb` : `${API}/media/${w.id}/raw`;
        const res = await api.get(url, { responseType: "blob" });
        const blob = res.data;
        await guard(offlineStore.putMedia({ id: w.id, conversation_id: w.conv, kind: w.kind, quality: w.quality, blob, size: blob.size, created_at: w.created_at }));
        forgetMediaUrl(w.id);
        urlMap.set(w.id, { url: URL.createObjectURL(blob), quality: w.quality });
      } catch (e) {
        if (state.full) return;
      }
    }
  };
  await Promise.all([worker(), worker(), worker()]);
}

// ---- full / incremental sync ----
let running = null;
export function syncNow(user, { full = false } = {}) {
  if (!user || !offlineEnabled(user.id) || state.full) return Promise.resolve();
  if (running) return running;
  running = (async () => {
    const settings = getOfflineSettings(user.id);
    setState({ syncing: true, progress: 0, label: "convs" });
    try {
      await hydrateMedia();
      const res = await api.get(`/conversations?user_id=${user.id}`);
      const convs = res.data || [];
      await guard(offlineStore.putConversations(convs));
      const cursors = (await offlineStore.getMeta("cursors")) || {};
      const start = historyStart(settings.history || "90d");
      const list = convs.filter((c) => c.type !== "buddy");
      for (let idx = 0; idx < list.length; idx++) {
        const c = list[idx];
        const lastAt = c.last_message?.created_at || c.updated_at || "";
        const cursor = full ? null : cursors[c.id];
        if (cursor && lastAt && lastAt <= cursor) { setState({ progress: Math.round(((idx + 1) / list.length) * 100) }); continue; }
        let after = cursor || start;
        let newest = cursor || "";
        if (cursor) {
          // messages hard-deleted by anyone since the cursor → drop them from the mirror too
          try {
            const del = await api.get(`/conversations/${c.id}/deleted`, { params: { since: cursor } });
            for (const id of del.data?.ids || []) await offlineStore.deleteMessage(id).catch(() => {});
          } catch (e) { /* ignore */ }
        }
        for (let page = 0; page < 50; page++) {
          // changed_since = new OR edited since the cursor (edits by others reach the mirror)
          const r = await api.get(`/conversations/${c.id}/messages`, { params: cursor && page === 0 ? { changed_since: after, limit: 2000 } : { after, limit: 2000 } });
          const msgs = (r.data || []).filter((m) => m.conversation_id === c.id);
          if (msgs.length) {
            await guard(offlineStore.putMessages(msgs));
            await cacheMediaFor(msgs, settings);
            newest = msgs.reduce((mx, m) => (m.created_at > mx ? m.created_at : mx), newest);
          }
          if ((r.data || []).length < 2000) break;
          after = newest;
        }
        cursors[c.id] = newest || cursors[c.id] || start;
        await offlineStore.setMeta("cursors", cursors);
        setState({ progress: Math.round(((idx + 1) / list.length) * 100) });
      }
      // conversations that vanished on the server
      const ids = new Set(convs.map((c) => c.id));
      for (const local of await offlineStore.getConversations()) {
        if (!ids.has(local.id)) { await offlineStore.deleteConversation(local.id); await offlineStore.deleteMessagesOf(local.id); await offlineStore.deleteMediaOf(local.id); }
      }
      setState({ syncing: false, progress: 100, lastSync: Date.now() });
    } catch (e) {
      setState({ syncing: false });
    } finally { running = null; }
  })();
  return running;
}

// ---- housekeeping ----
export async function clearOfflineData(uid) {
  urlMap.forEach((h) => { try { URL.revokeObjectURL(h.url); } catch (e) { /* ignore */ } });
  urlMap.clear();
  await offlineStore.clearAll().catch(() => {});
  setState({ full: false, progress: 0, lastSync: null });
  emit("media", { count: 0 });
}
export async function clearMediaOnly() {
  urlMap.forEach((h) => { try { URL.revokeObjectURL(h.url); } catch (e) { /* ignore */ } });
  urlMap.clear();
  await offlineStore.clearMedia().catch(() => {});
  setState({ full: false });
  emit("media", { count: 0 });
}
export async function clearMediaOlderThan(days) {
  const cutoff = new Date(Date.now() - days * 86400000).toISOString();
  const all = await offlineStore.getAllMedia().catch(() => []);
  for (const m of all) if ((m.created_at || "") < cutoff) { await offlineStore.deleteMedia(m.id).catch(() => {}); forgetMediaUrl(m.id); }
  setState({ full: false });
}
export async function clearConversationCache(convId) {
  await offlineStore.deleteMessagesOf(convId).catch(() => {});
  const all = await offlineStore.getAllMedia().catch(() => []);
  all.filter((m) => m.conversation_id === convId).forEach((m) => forgetMediaUrl(m.id));
  await offlineStore.deleteMediaOf(convId).catch(() => {});
  setState({ full: false });
}
export const usage = () => offlineStore.usage();
export const fmtBytes = (b) => (b > 1e9 ? `${(b / 1e9).toFixed(2)} GB` : b > 1e6 ? `${(b / 1e6).toFixed(1)} MB` : `${Math.round(b / 1e3)} KB`);

// ---- offline search over the mirror (used when the server is unreachable) ----
const norm = (t) => (t || "").toString().normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
export async function searchMirror(q, convId = null) {
  const needle = norm(q);
  if (!needle) return [];
  const all = await readConversations();
  const convs = convId ? all.filter((c) => c.id === convId).concat(all.some((c) => c.id === convId) ? [] : [{ id: convId }]) : all;
  const names = {};
  all.forEach((c) => (c.participants || []).forEach((p) => { if (p?.id) names[p.id] = p.name; }));
  const out = [];
  for (const c of convs) {
    const msgs = await readMessages(c.id);
    for (const m of msgs) {
      const hay = norm(`${m.content || ""} ${m.meta?.title || ""} ${(m.media || []).map((x) => x.name || x.filename || "").join(" ")}`);
      if (hay.includes(needle)) out.push({ ...m, sender_name: m.sender_name || names[m.sender_id] || "", conversation_name: c.name || c.other_user?.name || "" });
    }
  }
  out.sort((a, b) => (a.created_at > b.created_at ? 1 : -1));
  return out;
}
